import React, { useState, useEffect } from 'react';
import { Box, TableCell, styled, IconButton, Popover, TextField, InputAdornment, Typography } from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import FilterListIcon from '@mui/icons-material/FilterList';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import SearchIcon from '@mui/icons-material/Search';
import PushPinIcon from '@mui/icons-material/PushPin';

const HeaderTableCell = styled(TableCell, {
  shouldForwardProp: (prop) => prop !== 'isFirst' && prop !== 'isLast'
})(({ theme, isFirst, isLast }) => ({
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  fontWeight: 700,
  fontSize: '0.65rem',
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  padding: '6px 10px',
  color: '#ffffff',
  borderBottom: '2px solid #033263',
  whiteSpace: 'nowrap',
  position: 'sticky',
  top: 0,
  zIndex: 20,
  backgroundColor: '#0655A5',
  backgroundImage: 'linear-gradient(180deg, #075ea6 0%, #044384 100%)',
  boxShadow: 'none',
  userSelect: 'none',
  transition: 'all 0.2s ease',
  borderTopLeftRadius: isFirst ? '10px' : 0,
  borderTopRightRadius: isLast ? '10px' : 0,
  '&:hover': {
    backgroundColor: '#044384',
    backgroundImage: 'linear-gradient(180deg, #0968b8 0%, #03386e 100%)',
  }
}));

const getStickyStyles = (meta, isFirst = false, isLast = false) => {
  if (!meta) return {};
  const isPinned = meta.isPinned;
  return {
    position: 'sticky',
    top: 0,
    left: meta.left,
    zIndex: meta.zIndex + 20,
    minWidth: meta.width,
    width: meta.width,
    maxWidth: meta.width,
    backgroundColor: isPinned ? '#044384' : '#0655A5',
    backgroundImage: isPinned 
      ? 'linear-gradient(180deg, #0968b8 0%, #03386e 100%)' 
      : 'linear-gradient(180deg, #075ea6 0%, #044384 100%)',
    backdropFilter: isPinned ? 'blur(16px)' : 'none',
    boxShadow: meta.isLastSticky 
      ? '4px 0 10px -2px rgba(0,0,0,0.25)' 
      : (isPinned ? 'inset -1px 0 0 rgba(255,255,255,0.12)' : 'none'),
    borderRight: isPinned ? '1px solid rgba(255,255,255,0.12)' : 'none',
    transition: 'all 0.3s ease-in-out',
    borderTopLeftRadius: isFirst ? '10px' : 0,
    borderTopRightRadius: isLast ? '10px' : 0,
  };
};

const getColumnWidthStyles = (key) => {
  switch (key) {
    case 'TESORERO_ID_FOLIO_EMPRESA':
      return { width: '120px', minWidth: '100px', maxWidth: '140px' };
    case 'ID':
    case 'ACTIVO':
      return { width: '80px', minWidth: '65px', maxWidth: '90px', textAlign: 'center' };
    case 'FACTURA_COMPACTA':
      return { width: '150px', minWidth: '130px', maxWidth: '180px' };
    case 'FACTURA_PAGO':
      return { width: '180px', minWidth: '150px' };
    case 'MONTOS_RESUMEN':
      return { width: '165px', minWidth: '150px' };
    case 'TOTAL_DIVISA':
      return { width: '130px', minWidth: '115px' };
    case 'CENTRO_DEPTO':
      return { width: '220px', minWidth: '170px' };
    case 'PROVEEDOR_COMPRADOR':
      return { width: '240px', minWidth: '180px' };
    case 'FECHA_SOLICITANTE':
      return { width: '135px', minWidth: '115px' };
    case 'OC_REQUI':
      return { width: '115px', minWidth: '95px' };
    case 'ESTATUS':
    case 'ESTATUS_COMPACTO':
      return { width: '150px', minWidth: '120px', textAlign: 'center' };
    default:
      return {};
  }
};

export const TableColumnHeader = ({
  columnKey,
  label,
  isStickyMeta,
  sortConfig,
  onSort,
  enableSorting,
  enableFiltering,
  filterValue,
  onFilterChange,
  isFirst,
  isLast,
  isPrimaryCol,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [tempSearch, setTempSearch] = useState(filterValue || '');

  useEffect(() => {
    setTempSearch(filterValue || '');
  }, [filterValue]);

  const handleOpenFilter = (e) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleCloseFilter = () => {
    setAnchorEl(null);
    if (tempSearch !== (filterValue || '')) {
      onFilterChange(columnKey, tempSearch);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      onFilterChange(columnKey, tempSearch);
      handleCloseFilter();
    }
  };

  const clearFilter = (e) => {
    if (e) e.stopPropagation();
    setTempSearch('');
    onFilterChange(columnKey, '');
    setAnchorEl(null);
  };

  const isSorted = sortConfig && sortConfig.field === columnKey;
  const sortDir = sortConfig ? sortConfig.direction : 'asc';
  const hasFilter = Boolean(filterValue);
  const isCompactCol = columnKey === 'ID' || columnKey === 'ACTIVO' || columnKey === 'ESTATUS';
  const customWidths = getColumnWidthStyles(columnKey);

  return (
    <HeaderTableCell
      isFirst={isFirst}
      isLast={isLast}
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: isStickyMeta ? (isStickyMeta.zIndex + 20) : 20,
        ...(isStickyMeta ? getStickyStyles(isStickyMeta, isFirst, isLast) : {}),
        cursor: enableSorting ? 'pointer' : 'default',
        width: isStickyMeta ? isStickyMeta.width : (customWidths.width || 'auto'),
        minWidth: isStickyMeta ? isStickyMeta.width : (customWidths.minWidth || 'unset'),
        maxWidth: isStickyMeta ? isStickyMeta.width : (customWidths.maxWidth || 'none'),
        textAlign: isCompactCol ? 'center' : (customWidths.textAlign || 'left'),
        ...customWidths,
        '& .MuiIconButton-root': {
          opacity: hasFilter ? 1 : 0.4,
          '&:hover': { opacity: 1, backgroundColor: 'rgba(255,255,255,0.15)' }
        },
        '&:hover .MuiIconButton-root': {
          opacity: 1
        }
      }}
      onClick={() => enableSorting && onSort(columnKey)}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: isCompactCol ? 'center' : 'space-between', gap: 0.75 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flex: isPrimaryCol ? 1 : 'initial', position: 'relative' }}>
          {isStickyMeta?.isPinned && (
            <PushPinIcon sx={{ fontSize: 13, mr: 0.75, transform: 'rotate(45deg)', opacity: 0.8 }} />
          )}
          <Typography
            component="span"
            sx={{
              whiteSpace: 'nowrap',
              fontSize: 'inherit',
              fontWeight: 'inherit',
              letterSpacing: 'inherit',
            }}
          >
            {label}
          </Typography>
          {enableSorting && (
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                ml: 0.75,
                opacity: isSorted ? 1 : 0.3,
                transition: 'opacity 0.2s',
                '& svg': { fontSize: 15 }
              }}
            >
              {isSorted && sortDir === 'desc' ? <ArrowDownwardIcon /> : <ArrowUpwardIcon />}
            </Box>
          )}
        </Box>

        {enableFiltering && (
          <>
            <IconButton
              size="small"
              className="filter-btn"
              onClick={handleOpenFilter}
              sx={{
                p: 0.3,
                color: hasFilter ? '#fbbf24' : 'inherit',
                bgcolor: hasFilter ? 'rgba(251, 191, 36, 0.15)' : 'transparent',
              }}
            >
              <FilterListIcon sx={{ fontSize: 14 }} />
            </IconButton>

            <Popover
              open={Boolean(anchorEl)}
              anchorEl={anchorEl}
              onClose={handleCloseFilter}
              disableRestoreFocus
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{
                sx: {
                  p: 1.5,
                  width: 220,
                  borderRadius: 2,
                  boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
                  bgcolor: '#ffffff'
                }
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <TextField
                  autoFocus
                  size="small"
                  placeholder={`Buscar ${label}...`}
                  value={tempSearch}
                  onChange={(e) => setTempSearch(e.target.value)}
                  onKeyDown={handleKeyDown}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      </InputAdornment>
                    ),
                    endAdornment: tempSearch ? (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={clearFilter} edge="end">
                          <FilterAltOffIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </InputAdornment>
                    ) : null
                  }}
                  sx={{
                    '& .MuiInputBase-input': { fontSize: '0.8rem' }
                  }}
                />
              </Box>
            </Popover>
          </>
        )}
      </Box>
    </HeaderTableCell>
  );
};
